import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  ExternalLink,
  Pencil,
  FolderOpen,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/shared/page-header'
import { EmptyState } from '@/components/shared/empty-state'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { cn } from '@/lib/utils/cn'
import { CreateProjectWizard } from '@/features/projects/components/create-project-wizard'

// ─── Types ───────────────────────────────────────────────────────────────────

type ProjectStatus = 'active' | 'paused' | 'closed' | 'archived'

interface AdminProject {
  id: string
  name: string
  code: string | null
  department: string | null
  color: string | null
  status: ProjectStatus
  created_at: string
  project_number: number
  recordCount: number
}

interface ProjectFormState {
  name: string
  code: string
  department: string
  color: string
  status: ProjectStatus
}

const DEFAULT_FORM: ProjectFormState = {
  name: '',
  code: '',
  department: '',
  color: '#274690',
  status: 'active',
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  active: 'فعال',
  paused: 'متوقف',
  closed: 'بسته',
  archived: 'بایگانی',
}

const STATUS_VARIANTS: Record<ProjectStatus, string> = {
  active: 'bg-green-500/15 text-green-400 border-green-500/30',
  paused: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  closed: 'bg-red-500/15 text-red-400 border-red-500/30',
  archived: 'bg-muted text-muted-foreground border-border',
}

// ─── Queries ─────────────────────────────────────────────────────────────────

async function fetchAdminProjects(): Promise<AdminProject[]> {
  const [projectsRes, recordsRes] = await Promise.all([
    supabase
      .from('projects')
      .select('id, name, code, department, color, status, created_at, project_number')
      .order('project_number', { ascending: true }),
    supabase
      .from('records')
      .select('project_id')
      .eq('is_deleted', false),
  ])

  const projects = projectsRes.data ?? []
  const records = recordsRes.data ?? []

  // Count records per project client-side to avoid N+1
  const countByProject: Record<string, number> = {}
  for (const r of records) {
    if (r.project_id) {
      countByProject[r.project_id] = (countByProject[r.project_id] ?? 0) + 1
    }
  }

  return projects.map((p) => ({
    ...p,
    status: p.status as ProjectStatus,
    recordCount: countByProject[p.id] ?? 0,
  }))
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface ProjectFormModalProps {
  open: boolean
  onClose: () => void
  initial?: (ProjectFormState & { id: string; version: number }) | null
}

function ProjectFormModal({ open, onClose, initial }: ProjectFormModalProps) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [form, setForm] = useState<ProjectFormState>(
    initial
      ? {
          name: initial.name,
          code: initial.code,
          department: initial.department,
          color: initial.color,
          status: initial.status,
        }
      : DEFAULT_FORM
  )
  const [error, setError] = useState<string | null>(null)

  const isEdit = !!initial

  const mutation = useMutation({
    mutationFn: async (values: ProjectFormState) => {
      if (isEdit && initial) {
        const { error: updateError } = await supabase
          .from('projects')
          .update({
            name: values.name,
            code: values.code || null,
            department: values.department || null,
            color: values.color,
            status: values.status,
            updated_by: user?.id,
          })
          .eq('id', initial.id)

        if (updateError) throw updateError
      } else {
        // project_number is set by sequence trigger; cast to bypass TS requiring it
        const { error: insertError } = await supabase
          .from('projects')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert({
            name: values.name,
            code: values.code || null,
            department: values.department || null,
            color: values.color,
            status: values.status,
            created_by: user!.id,
          })

        if (insertError) throw insertError
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] })
      onClose()
      setForm(DEFAULT_FORM)
      setError(null)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('نام پروژه الزامی است')
      return
    }
    setError(null)
    mutation.mutate(form)
  }

  const handleClose = () => {
    setForm(initial ?? DEFAULT_FORM)
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'ویرایش پروژه' : 'ایجاد پروژه جدید'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">نام پروژه *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="مثال: رجیستری سرطان"
            />
          </div>

          {/* Code */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">کد پروژه</label>
            <Input
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="مثال: CANREG"
              className="font-mono"
            />
          </div>

          {/* Department */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">دپارتمان</label>
            <Input
              value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
              placeholder="مثال: آنکولوژی"
            />
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">رنگ</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                className="w-10 h-10 rounded cursor-pointer border border-border bg-transparent p-0.5"
              />
              <span className="text-xs font-mono text-muted-foreground">{form.color}</span>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">وضعیت</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, status: s }))}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
                    form.status === s
                      ? STATUS_VARIANTS[s]
                      : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  )}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <DialogFooter className="gap-2 mt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              لغو
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'در حال ذخیره...' : isEdit ? 'ذخیره تغییرات' : 'ایجاد پروژه'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PlatformProjectsPage() {
  const navigate = useNavigate()
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: fetchAdminProjects,
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<(ProjectFormState & { id: string; version: number }) | null>(null)

  return (
    <div className="p-6">
      <PageHeader
        title="پروژه‌ها"
        subtitle="مدیریت تمام پروژه‌های ثبت شده در پلتفرم"
        actions={
          <Button onClick={() => setCreateOpen(true)} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" />
            ایجاد پروژه
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="h-5 bg-muted rounded w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={FolderOpen}
            title="هیچ پروژه‌ای وجود ندارد"
            description="اولین پروژه را ایجاد کنید"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors"
            >
              {/* Header */}
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold font-mono shrink-0"
                  style={{ backgroundColor: project.color ?? '#274690' }}
                >
                  {project.code?.slice(0, 3).toUpperCase() ?? '#'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate leading-tight">
                    {project.name}
                  </p>
                  {project.department && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {project.department}
                    </p>
                  )}
                </div>
              </div>

              {/* Status + record count */}
              <div className="flex items-center gap-2">
                <Badge
                  className={cn(
                    'text-[11px] font-medium border px-2 py-0.5 rounded-md',
                    STATUS_VARIANTS[project.status]
                  )}
                >
                  {STATUS_LABELS[project.status]}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono ms-auto">
                  {project.recordCount.toLocaleString('fa-IR')} سابقه
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-border/60">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-xs text-muted-foreground hover:text-foreground flex-1"
                  onClick={() => navigate(`/projects/${project.id}/overview`)}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  باز کردن
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-xs text-muted-foreground hover:text-foreground flex-1"
                  onClick={() =>
                    setEditTarget({
                      id: project.id,
                      name: project.name,
                      code: project.code ?? '',
                      department: project.department ?? '',
                      color: project.color ?? '#274690',
                      status: project.status,
                      version: 0,
                    })
                  }
                >
                  <Pencil className="w-3.5 h-3.5" />
                  ویرایش
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create wizard */}
      <CreateProjectWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {/* Edit modal */}
      <ProjectFormModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        initial={editTarget}
      />
    </div>
  )
}
